import { AssignRepToCompanies, EditCompany, GetCompany, RegisterCompany } from "@/dtos/Company.dto";
import { StateObject } from "@/dtos/State.dto";
import Company from "@/services/company/company";
import {
  ASSIGN_REP_TO_COMPANIES,
  CLEAR_COMPANY_ISLOADING,
  DELETE_COMPANY,
  EDIT_COMPANY,
  GET_COMPANIES,
  GET_COMPANY,
  REGISTER_COMPANY,
} from "@/store/action-types";
import { SET_COMPANIES, SET_COMPANY, SET_ERRORMSG } from "@/store/mutation-types";
import { AxiosResponse } from "axios";
import { Module } from "vuex";

const company: Module<any, {}> = {
  state: {
    company: {
      data: null,
      isLoading: false,
    },
    companies: {
      data: null,
      metaData: null,
      isLoading: false,
    },
  },
  getters: {
    company: (state: { company: any }) => state.company,
    companies: (state: { companies: any }) => state.companies,
  },
  mutations: {
    [SET_COMPANY]: (
      state: { company: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.company.data = payload.data;
      state.company.isLoading = payload.isLoading;
    },
    [SET_COMPANIES]: (
      state: { companies: StateObject },
      payload: { data: null; metaData: any; isLoading: any }
    ) => {
      if (payload.data != null) state.companies.data = payload.data;
      if (payload.metaData != null) state.companies.metaData = payload.metaData;
      state.companies.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_COMPANY]: async ({ commit }: any, payload: any) => {
      commit(SET_COMPANY, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Company.registerCompany(payload as RegisterCompany);

        if (result) {
          if (result.data.status) {
            commit(SET_COMPANY, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_COMPANY, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_COMPANY, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_COMPANY, { data: null, isLoading: false });
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to register company");
        }
        return false;
      }
    },
    [GET_COMPANIES]: async ({ commit }: any, payload: any) => {
      commit(SET_COMPANIES, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Company.getCompanies(payload as GetCompany);

        if (result) {
          if (result.data.status) {
            commit(SET_COMPANIES, {
              data: result.data.data,
              metaData: result.data.metaData,
              isLoading: false,
            });
            return true;
          }
          commit(SET_COMPANIES, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_COMPANIES, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_COMPANIES, { data: null, isLoading: false });
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load companies");
        }
        return false;
      }
    },
    [GET_COMPANY]: async ({ commit }: any, payload: any) => {
      commit(SET_COMPANY, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Company.getCompanies(payload as GetCompany);

        if (result) {
          if (result.data.status) {
            commit(SET_COMPANY, { data: result.data.data[0], isLoading: false });
            return true;
          }
          commit(SET_COMPANY, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_COMPANY, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_COMPANY, { data: null, isLoading: false });
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load company");
        }
        return false;
      }
    },
    [EDIT_COMPANY]: async ({ commit }: any, payload: any) => {
      commit(SET_COMPANY, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Company.editCompany(payload as EditCompany);

        if (result) {
          if (result.data.status) {
            commit(SET_COMPANY, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_COMPANY, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_COMPANY, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_COMPANY, { data: null, isLoading: false });
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to edit company");
        }
        return false;
      }
    },
    [DELETE_COMPANY]: async ({ commit }: any, payload: string) => {
      commit(SET_COMPANY, { data: { id: payload }, isLoading: true });

      try {
        const result: AxiosResponse = await Company.deleteCompany(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_COMPANY, { data: result.data.data, isLoading: false });
            return true;
          }
          commit(SET_COMPANY, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_COMPANY, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_COMPANY, { data: null, isLoading: false });
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to delete company");
        }
        return false;
      }
    },
    [ASSIGN_REP_TO_COMPANIES]: async ({ commit }: any, payload: AssignRepToCompanies) => {
      commit(SET_COMPANIES, { isLoading: true });

      try {
        const result: AxiosResponse = await Company.assignRepToCompanies(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_COMPANIES, { isLoading: false });
            return true;
          }
          commit(SET_COMPANIES, { isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_COMPANIES, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_COMPANIES, { isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to assign rep to companies"
          );
        }
        return false;
      }
    },
    [CLEAR_COMPANY_ISLOADING]: ({ commit }) => {
      commit(SET_COMPANY, { isLoading: false });
      commit(SET_COMPANIES, { isLoading: false });
    },
  },
};

export default company;
